import { cn } from "@/lib/utils";
import { resolveMediaImageUrl } from "@/lib/resolve-media-image-url";
import { Testimonial } from "@/services/testimonials/types";

import { creatorInitials } from "./creator-initials";
import { formatMemberSince } from "./format-member-since";
import { parseQuoteEmphasis } from "./parse-quote-emphasis";
import { HOME_COPY } from "../home.copy";

/**
 * A receita de cada coluna: que papel cada lugar recebe e quais falam alto.
 * Os jogos de papel estão em `styles/home.style.css`.
 *
 * Se repete a cada seis lugares, então vale para qualquer quantidade de
 * depoimentos que a API devolva — seis, doze ou trinta. É por coluna, e não uma
 * lista corrida, porque as quatro precisam de sorteios diferentes: com a mesma
 * receita nas quatro, a parede parada ganha faixas atravessando a tela.
 *
 * Duas regras por trás dos números:
 * - a maioria dos recados fica no papel padrão. Cor é evento, não regra — se
 *   todo bilhete for colorido, nenhum é;
 * - as vozes altas nunca caem em bilhetes vizinhos, contando a volta do loop
 *   (o lugar 5 encosta no lugar 0 da repetição seguinte).
 */
const RECEITAS: { papeis: string[]; altas: number[] }[] = [
  {
    papeis: ["", "recado-verde", "", "", "recado-claro", "recado-rosa"],
    altas: [2, 5],
  },
  {
    papeis: ["", "", "recado-claro", "", "recado-verde", ""],
    altas: [0, 3],
  },
  {
    papeis: ["recado-laranja", "recado-escuro", "", "", "", "recado-claro"],
    altas: [1, 5],
  },
  {
    papeis: ["", "recado-roxo", "", "recado-verde", "", ""],
    altas: [0, 4],
  },
];

/** Tamanho do ciclo de uma receita. A parede usa isto para escolher quantos
 * recados cabem numa coluna sem quebrar a receita na emenda do loop. */
export const CICLO_DA_RECEITA = 6;

/**
 * Degraus de corpo da voz alta. Quem decide se o recado fala alto é o lugar na
 * parede; o comprimento só escolhe o tamanho — nunca cancela a voz.
 *
 * Já foi um teto ("acima de 190 caracteres volta para a voz normal") e isso
 * apagou a segunda voz da parede inteira: os depoimentos de verdade têm 191
 * caracteres. Um teto calibrado em texto de protótipo não sobrevive ao texto
 * real, então aqui não existe teto — a display encolhe até caber, e o limite
 * de cima é o do próprio campo na API, 2000 caracteres.
 */
const DEGRAUS_DA_VOZ_ALTA = [
  { ate: 105, classe: "text-[23px] leading-[1.12] sm:text-[27px]" },
  { ate: 190, classe: "text-[19px] leading-[1.16] sm:text-[21px]" },
  { ate: Infinity, classe: "text-[17px] leading-[1.22] sm:text-[18px]" },
];

interface TestimonialNoteProps {
  testimonial: Testimonial;
  /** Qual coluna da parede — decide a receita de papéis e vozes. */
  coluna: number;
  /**
   * Lugar dentro da coluna. É o lugar, e não o índice do depoimento, que decide
   * papel, voz e inclinação: assim o mesmo depoimento repetido em duas colunas
   * nunca sai idêntico.
   */
  lugar: number;
  /** Atraso de entrada da coluna inteira. */
  atraso: string;
  animarEntrada: boolean;
}

/**
 * Inclinação determinística entre -1,5° e 1,5°. Determinística porque
 * `Math.random` no render quebraria a hidratação — e porque bilhete pregado não
 * muda de ângulo quando você recarrega a página.
 */
function inclinacao(semente: number): string {
  return `${((((semente * 37) % 31) - 15) / 10).toFixed(2)}deg`;
}

export function TestimonialNote({
  testimonial,
  coluna,
  lugar,
  atraso,
  animarEntrada,
}: TestimonialNoteProps) {
  const receita = RECEITAS[coluna % RECEITAS.length];
  const passo = lugar % CICLO_DA_RECEITA;
  const papel = receita.papeis[passo];
  const vozAlta = receita.altas.includes(passo);
  const corpoDaVozAlta = DEGRAUS_DA_VOZ_ALTA.find(
    (degrau) => testimonial.quote.length <= degrau.ate,
  );
  const parts = parseQuoteEmphasis(testimonial.quote);
  const memberSince = formatMemberSince(testimonial.memberSince);
  const photo = resolveMediaImageUrl(testimonial.photoUrl);

  return (
    <figure
      className={cn(
        "recado rounded-2xl px-5 pb-4 pt-5",
        papel,
        vozAlta && "recado-alto px-[22px] pb-[18px] pt-6",
        animarEntrada && "recado-assenta",
      )}
      style={{
        ["--rot" as string]: inclinacao(coluna * CICLO_DA_RECEITA + lugar),
        ["--i" as string]: lugar,
        ["--atraso" as string]: atraso,
      }}
    >
      {/*
        A segunda voz da parede: a display da casa em corpo grande. É o que
        impede o mural de virar uma coleção de parágrafos do mesmo tamanho.
      */}
      <blockquote
        className={cn(
          "text-[17.5px] leading-[1.42] tracking-[-0.004em]",
          // A voz vem da tipografia, não do corpo: mesmo no degrau mais baixo o
          // recado continua na display e em negrito, e continua sendo a
          // segunda voz da parede.
          vozAlta && "font-hsmische font-bold tracking-[-0.02em]",
          vozAlta && corpoDaVozAlta?.classe,
        )}
      >
        {parts.map((part, partIndex) =>
          part.emphasis ? (
            <strong
              key={`${testimonial.id}-${partIndex}`}
              className="recado-marca font-extrabold italic"
            >
              {part.text}
            </strong>
          ) : (
            <span key={`${testimonial.id}-${partIndex}`}>{part.text}</span>
          ),
        )}
      </blockquote>

      <hr className="recado-fio mb-3 mt-4 border-t" />

      <figcaption className="flex items-center gap-2.5">
        {photo ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={photo}
            alt=""
            loading="lazy"
            className="recado-fio size-[34px] shrink-0 rounded-full border object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="recado-iniciais recado-fio flex size-[34px] shrink-0 select-none items-center justify-center rounded-full border bg-tami-green text-xs font-bold text-black"
          >
            {creatorInitials(testimonial.displayName)}
          </span>
        )}

        <div className="min-w-0">
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-sm font-bold leading-tight tracking-[-0.005em]">
            <span className="min-w-0 max-w-full truncate">
              {testimonial.displayName}
            </span>
            {testimonial.socialHandle ? (
              <span className="min-w-0 max-w-full truncate text-xs font-semibold opacity-75">
                @{testimonial.socialHandle}
              </span>
            ) : null}
          </p>
          {memberSince ? (
            <p className="recado-secundario mt-0.5 text-xs leading-[1.3]">
              {HOME_COPY.testimonials.memberSince}{" "}
              <time dateTime={testimonial.memberSince ?? undefined}>
                {memberSince}
              </time>
            </p>
          ) : null}
        </div>
      </figcaption>
    </figure>
  );
}
